import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "author" });
  const [loading, setLoading] = useState(false);
  const [regEnabled, setRegEnabled] = useState(true);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/system/settings");
        setRegEnabled(!!data.registration_enabled);
      } catch {
        setRegEnabled(true);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form.email, form.password, form.name, form.role);
      toast.success(`Welcome to Bookforge, ${user.name}`);
      if (user.role === "publisher") {
        navigate("/publisher");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ background: "var(--bf-bg)" }}
      data-testid="register-page"
    >
      <div
        className="w-full max-w-md p-10 rounded-lg"
        style={{ background: "white", border: "1px solid var(--bf-border)" }}
      >
        <Link to="/" className="font-serif text-2xl mb-2 block" data-testid="register-brand">Bookforge</Link>
        <h1 className="font-serif text-3xl mb-1">Create your account</h1>
        <p className="text-sm mb-6" style={{ color: "var(--bf-text-muted)" }}>
          Start your first book in less than a minute.
        </p>

        {/* Role chooser */}
        <div className="flex gap-2 mb-6 p-1 rounded-md" style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}>
          <button
            type="button"
            onClick={() => setForm({ ...form, role: "author" })}
            className="flex-1 py-2 rounded text-sm transition-colors"
            style={{
              background: form.role === "author" ? "white" : "transparent",
              color: form.role === "author" ? "var(--bf-primary)" : "var(--bf-text-muted)",
              fontWeight: form.role === "author" ? 600 : 400,
              boxShadow: form.role === "author" ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
            }}
            data-testid="register-role-author"
          >
            I&apos;m an Author
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, role: "publisher" })}
            className="flex-1 py-2 rounded text-sm transition-colors"
            style={{
              background: form.role === "publisher" ? "white" : "transparent",
              color: form.role === "publisher" ? "var(--bf-primary)" : "var(--bf-text-muted)",
              fontWeight: form.role === "publisher" ? 600 : 400,
              boxShadow: form.role === "publisher" ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
            }}
            data-testid="register-role-publisher"
          >
            I&apos;m a Publisher
          </button>
        </div>
        {form.role === "publisher" && (
          <p
            className="text-xs mb-5 p-3 rounded"
            style={{ background: "#FEF7E7", border: "1px solid #F1D78E", color: "#7A5B17" }}
            data-testid="register-publisher-note"
          >
            Publisher accounts go through an admin approval step before appearing in our public directory.
            You can fill in your profile right after sign-up.
          </p>
        )}

        {!checking && !regEnabled && (
          <div className="p-4 rounded mb-6" style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }} data-testid="register-disabled-banner">
            <p className="text-sm font-medium mb-1">Public sign-up is currently closed.</p>
            <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
              Existing accounts can still sign in. Contact an admin for an invite.
            </p>
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          className="w-full h-11 mb-5"
          onClick={googleLogin}
          disabled={!regEnabled || checking}
          data-testid="register-google-btn"
        >
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-5 text-xs" style={{ color: "var(--bf-text-muted)" }}>
          <div className="flex-1 h-px" style={{ background: "var(--bf-border)" }} />
          OR EMAIL
          <div className="flex-1 h-px" style={{ background: "var(--bf-border)" }} />
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="name" className="mb-2 block">{form.role === "publisher" ? "Publisher / Imprint name" : "Author name"}</Label>
            <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" />
          </div>
          <div>
            <Label htmlFor="email" className="mb-2 block">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
          </div>
          <div>
            <Label htmlFor="password" className="mb-2 block">Password</Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
          </div>
          <Button
            type="submit"
            disabled={loading || !regEnabled || checking}
            className="w-full h-11 text-white"
            style={{ background: "var(--bf-primary)" }}
            data-testid="register-submit-btn"
          >
            {loading ? "Creating account..." : "Create account"}
          </Button>
        </form>

        <p className="text-sm mt-6 text-center" style={{ color: "var(--bf-text-muted)" }}>
          Already have an account?{" "}
          <Link to="/login" className="underline" data-testid="register-to-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
